import React, { createContext, useContext, useRef, useEffect, useState } from 'react';
import { PropertyValueManager, type PropertyValueManagerConfig } from './PropertyValueManager';
import type { ValueChangeEvent } from './types';

interface PropertyValueContextType {
  valueManager: PropertyValueManager | null;
  values: Record<string, any>;
  errors: Record<string, string[]>;
  pageValidation: Record<string, boolean>;
  handleValueChange: (event: ValueChangeEvent) => void;
  getValue: (componentId: string) => any;
  getComponentErrors: (componentId: string) => string[] | undefined;
  isPageValid: (pageId: string) => boolean;
  hasRequiredFieldsError: (pageId: string) => boolean;
  validateAllPages: () => { isValid: boolean; errors: Record<string, string[]> };
  reset: (newValues?: Record<string, any>) => void;
}

const PropertyValueContext = createContext<PropertyValueContextType | null>(null);

interface PropertyValueProviderProps {
  config: PropertyValueManagerConfig;
  children: React.ReactNode;
}

export const PropertyValueProvider: React.FC<PropertyValueProviderProps> = ({ 
  config, 
  children 
}) => {
  const valueManagerRef = useRef<PropertyValueManager | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [pageValidation, setPageValidation] = useState<Record<string, boolean>>({});

  // Initialize or update value manager when config changes
  useEffect(() => {
    if (!valueManagerRef.current) {
      // Create new value manager
      valueManagerRef.current = new PropertyValueManager(config);
    } else {
      // Update existing value manager with new config
      valueManagerRef.current = new PropertyValueManager({
        ...config,
        initialValues: valueManagerRef.current.getValues()
      });
    }

    const manager = valueManagerRef.current;

    // Set initial state
    setValues(manager.getValues());
    setErrors(manager.getErrors());
    setPageValidation(manager.getPageValidation());

    // Set up listeners
    const unsubscribeValues = manager.addChangeListener((newValues) => {
      setValues(newValues);
    });

    const unsubscribeValidation = manager.addValidationListener((newValidation) => {
      setPageValidation(newValidation);
      setErrors(manager.getErrors());
    });

    // Cleanup function
    return () => {
      unsubscribeValues();
      unsubscribeValidation();
    };
  }, [config]);

  const handleValueChange = (event: ValueChangeEvent): void => {
    valueManagerRef.current?.handleValueChange(event);
  };

  const getValue = (componentId: string): any => {
    return valueManagerRef.current?.getValue(componentId);
  };

  const getComponentErrors = (componentId: string): string[] | undefined => {
    return valueManagerRef.current?.getComponentErrors(componentId);
  };

  const isPageValid = (pageId: string): boolean => {
    return valueManagerRef.current?.isPageValid(pageId) ?? false;
  };

  const hasRequiredFieldsError = (pageId: string): boolean => {
    const manager = valueManagerRef.current;
    if (!manager) return false;
    
    const page = config.pages.find(p => p.id === pageId);
    return page ? manager.hasRequiredFieldsError(page) : false;
  };

  const validateAllPages = () => {
    return valueManagerRef.current?.validateAllPages() ?? { isValid: false, errors: {} };
  };

  const reset = (newValues?: Record<string, any>): void => {
    valueManagerRef.current?.reset(newValues);
  };

  const contextValue: PropertyValueContextType = {
    valueManager: valueManagerRef.current,
    values,
    errors,
    pageValidation,
    handleValueChange,
    getValue,
    getComponentErrors,
    isPageValid,
    hasRequiredFieldsError,
    validateAllPages,
    reset
  };

  return (
    <PropertyValueContext.Provider value={contextValue}>
      {children}
    </PropertyValueContext.Provider>
  );
};

export const usePropertyValues = (): PropertyValueContextType => {
  const context = useContext(PropertyValueContext);
  if (!context) {
    throw new Error('usePropertyValues must be used within a PropertyValueProvider');
  }
  return context;
};

// Hook for getting a specific component's value and error state
export const useComponentValue = (componentId: string) => {
  const { getValue, getComponentErrors, handleValueChange } = usePropertyValues();
  
  return {
    value: getValue(componentId),
    error: getComponentErrors(componentId),
    setValue: (value: any, pageId: string) => {
      handleValueChange({ componentId, value, pageId });
    }
  };
};

// Hook for getting page validation state
export const usePageValidation = (pageId: string) => {
  const { isPageValid, hasRequiredFieldsError, pageValidation } = usePropertyValues();
  
  return {
    isValid: isPageValid(pageId),
    hasRequiredFieldsError: hasRequiredFieldsError(pageId),
    validationState: pageValidation[pageId]
  };
}; 